import React, { useState } from 'react';
import { 
  DataGrid, 
  GridColDef, 
  GridRowsProp, 
  GridToolbar,
  GridPaginationModel,
  GridInitialState
} from '@mui/x-data-grid';
import { Box, Paper } from '@mui/material';
import { deDE, enUS } from '@mui/x-data-grid/locales';
import { useI18n } from '@/hooks/useI18n';

interface ReusableDataGridProps {
  rows: GridRowsProp;
  columns: GridColDef[];
  loading?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  disableColumnFilter?: boolean;
  disableColumnSelector?: boolean;
  disableDensitySelector?: boolean;
  disableExport?: boolean;
  hideFooter?: boolean;
  autoHeight?: boolean;
  rowHeight?: number;
  initialState?: GridInitialState;
  getRowId?: (row: GridRowsProp[number]) => string;
  onPaginationModelChange?: (model: GridPaginationModel) => void;
  paginationModel?: GridPaginationModel;
}

const ReusableDataGrid: React.FC<ReusableDataGridProps> = ({
  rows,
  columns,
  loading = false,
  pageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],
  disableColumnFilter = false,
  disableColumnSelector = false,
  disableDensitySelector = false,
  disableExport = false,
  hideFooter = false,
  rowHeight,
  initialState,
  getRowId,
  onPaginationModelChange,
  paginationModel,
}) => {
  const { i18n } = useI18n();
  
  // Get the appropriate locale for DataGrid based on current language
  const getDataGridLocale = () => {
    switch (i18n.language) {
      case 'de':
        return deDE.components.MuiDataGrid.defaultProps.localeText;
      case 'en':
        return enUS.components.MuiDataGrid.defaultProps.localeText;
      default:
        return deDE.components.MuiDataGrid.defaultProps.localeText; // Default to German
    }
  };

  // Internal state for pagination when no external state is provided
  const [internalPaginationModel, setInternalPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: pageSize,
  });

  // Use external paginationModel if provided, otherwise use internal state
  const currentPaginationModel = paginationModel || internalPaginationModel;

  // Handle pagination model changes
  const handlePaginationModelChange = (model: GridPaginationModel) => {
    if (onPaginationModelChange) {
      // If external handler is provided, use it
      onPaginationModelChange(model);
    } else {
      // Otherwise, update internal state
      setInternalPaginationModel(model);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper elevation={1} sx={{ borderRadius: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          checkboxSelection={false}
          disableRowSelectionOnClick
          getRowId={getRowId}
          paginationModel={currentPaginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={pageSizeOptions}
          hideFooter={hideFooter}
          {...(rowHeight && { rowHeight })}
          {...(initialState && { initialState })}
          localeText={getDataGridLocale()}
          slots={{
            toolbar: disableExport ? undefined : GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
              csvOptions: {
                fileName: 'export',
                delimiter: ';',
                utf8WithBom: true,
              },
              printOptions: { disableToolbarButton: true },
              ...(disableColumnFilter && { disableColumnFilter: true }),
              ...(disableColumnSelector && { disableColumnSelector: true }),
              ...(disableDensitySelector && { disableDensitySelector: true }),
            },
          }}
          sx={{
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default ReusableDataGrid;
